import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Spinner, Badge, Form, Button, Modal } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSyncAlt, faExclamationTriangle, faCheckCircle,
  faFileInvoice, faBuilding, faTag, faClock, faTimesCircle,
  faSearch, faFilter, faTimes, faUsers, faDollarSign,
  faTrash, faLock, faEye, faDownload, faClock as faClockIcon,
  faMoneyBillWave
} from '@fortawesome/free-solid-svg-icons';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import API_CONFIG2 from '../../API_CONFIG/API_CONFIG2';
import Dexie from 'dexie';
import Logo from '../../../assetes/logos.png';

// ============================================================
// CACHE DEXIE (24h)
// ============================================================
const db = new Dexie('FacturesGestionCache');
db.version(1).stores({ factures: 'key, data, timestamp' });
const CACHE_DURATION = 24 * 60 * 60 * 1000;

async function getCached(key) {
  try {
    const entry = await db.factures.get(key);
    if (!entry) return null;
    if (Date.now() - entry.timestamp > CACHE_DURATION) return null;
    return entry.data;
  } catch { return null; }
}
async function setCached(key, data) {
  await db.factures.put({ key, data, timestamp: Date.now() });
}

// ============================================================
// COMPOSANT PRINCIPAL
// ============================================================
const API_URL = `${API_CONFIG2.apiUrl}placement/api_factures_gestion.php`;
const API_PARAMS_URL = `${API_CONFIG2.apiUrl}placement/api_parametres_entreprise.php`;

const GestionFactures = () => {
  // États principaux
  const [factures, setFactures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [dataSource, setDataSource] = useState('cache');
  const [updating, setUpdating] = useState({});
  const [deleting, setDeleting] = useState({});

  // Filtres
  const [searchText, setSearchText] = useState('');
  const [filterStatut, setFilterStatut] = useState('');
  const [dateDebut, setDateDebut] = useState('');
  const [dateFin, setDateFin] = useState('');

  // Facture en cours (modal)
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [selectedFacture, setSelectedFacture] = useState(null);
  const [companyParams, setCompanyParams] = useState(null);
  const [loadingInvoice, setLoadingInvoice] = useState(false);
  const [pdfGenerating, setPdfGenerating] = useState(false);

  // Référence pour l'aperçu HTML
  const invoiceRef = useRef(null);

  // Contrôle des requêtes
  const abortControllerRef = useRef(null);
  const isMounted = useRef(true);

  // ============================================================
  // RÉCUPÉRATION DES PARAMÈTRES ENTREPRISE
  // ============================================================
  useEffect(() => {
    const fetchParams = async () => {
      try {
        const res = await fetch(`${API_PARAMS_URL}?action=get`);
        const data = await res.json();
        if (data.status === 'success') {
          setCompanyParams(data.data);
        }
      } catch (e) {
        console.warn('Erreur chargement paramètres entreprise:', e);
      }
    };
    fetchParams();
  }, []);

  // ============================================================
  // RÉCUPÉRATION DES FACTURES
  // ============================================================
  const fetchFactures = useCallback(async (forceRefresh = false) => {
    if (abortControllerRef.current) abortControllerRef.current.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    if (forceRefresh) setRefreshing(true);
    else setLoading(true);

    const cacheKey = 'factures_liste';

    try {
      if (!forceRefresh) {
        const cached = await getCached(cacheKey);
        if (cached && Array.isArray(cached)) {
          setFactures(cached);
          setDataSource('cache');
          setLoading(false);
          setRefreshing(false);
          return;
        }
      }

      const url = `${API_URL}?action=liste&unique=${new Date().getTime()}`;
      const response = await fetch(url, { signal: controller.signal });
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        throw new Error(`Réponse non-JSON: ${text.substring(0, 100)}`);
      }
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      if (data.status === 'success') {
        const facturesData = Array.isArray(data.data) ? data.data : [];
        setFactures(facturesData);
        setDataSource(forceRefresh ? 'live (actualisé)' : 'live');
        await setCached(cacheKey, facturesData);
        setError(null);
      } else {
        throw new Error(data.message || 'Erreur inconnue');
      }
    } catch (err) {
      if (err.name === 'AbortError') return;
      setError(err.message);
      setFactures([]);
    } finally {
      if (isMounted.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, []);

  useEffect(() => {
    isMounted.current = true;
    fetchFactures();
    return () => {
      isMounted.current = false;
      if (abortControllerRef.current) abortControllerRef.current.abort();
    };
  }, [fetchFactures]);

  // ============================================================
  // MISE À JOUR STATUT
  // ============================================================
  const handleStatutChange = async (id, newStatut) => {
    setUpdating(prev => ({ ...prev, [id]: true }));
    try {
      const userId = 1; // À remplacer par l'ID de l'utilisateur connecté
      const response = await fetch(`${API_URL}?action=update_statut`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, statut: newStatut, userId })
      });
      const data = await response.json();
      if (data.status === 'success') {
        setFactures(prev => prev.map(f => f.id === id ? { ...f, statut: newStatut } : f));
      } else {
        throw new Error(data.message || 'Erreur');
      }
    } catch (err) {
      setError(err.message);
      setTimeout(() => setError(null), 4000);
    } finally {
      setUpdating(prev => ({ ...prev, [id]: false }));
    }
  };

  // ============================================================
  // SUPPRESSION D'UNE FACTURE
  // ============================================================
  const handleDelete = async (id, statut) => {
    if (!window.confirm(`⚠️ Supprimer définitivement la facture ${id} ?\n${statut === 'Payée' ? 'Cette facture est déjà payée. Un remboursement sera effectué.' : ''}`)) return;
    setDeleting(prev => ({ ...prev, [id]: true }));
    try {
      const userId = 1;
      const response = await fetch(`${API_URL}?action=delete&id=${id}&userId=${userId}`, {
        method: 'DELETE'
      });
      const data = await response.json();
      if (data.status === 'success') {
        setFactures(prev => prev.filter(f => f.id !== id));
      } else {
        throw new Error(data.message || 'Erreur');
      }
    } catch (err) {
      setError(err.message);
      setTimeout(() => setError(null), 4000);
    } finally {
      setDeleting(prev => ({ ...prev, [id]: false }));
    }
  };

  // ============================================================
  // OUVERTURE MODAL FACTURE
  // ============================================================
  const openInvoiceModal = async (factureId) => {
    setLoadingInvoice(true);
    try {
      const res = await fetch(`${API_URL}?action=single&id=${factureId}`);
      const data = await res.json();
      if (data.status === 'success') {
        setSelectedFacture(data.data);
        setShowInvoiceModal(true);
      } else {
        setError(data.message || 'Erreur');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingInvoice(false);
    }
  };

  // ============================================================
  // GÉNÉRATION PDF avec jsPDF + html2canvas
  // ============================================================
  const generatePDF = async () => {
    if (!selectedFacture || !invoiceRef.current) return;
    setPdfGenerating(true);
    try {
      const input = invoiceRef.current;
      const canvas = await html2canvas(input, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        logging: false,
        backgroundColor: '#ffffff',
      });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgWidth = 210;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
      pdf.save(`facture_${selectedFacture.reference}.pdf`);
    } catch (error) {
      console.error('Erreur génération PDF:', error);
      setError('Erreur lors de la génération du PDF');
      setTimeout(() => setError(null), 5000);
    } finally {
      setPdfGenerating(false);
    }
  };

  // ============================================================
  // FORMATAGE ET BADGES
  // ============================================================
  const formatCurrency = (value) => parseFloat(value).toFixed(2) + ' USD';

  const getStatutBadge = (statut) => {
    const config = {
      'En attente': { icon: faClock, variant: 'warning', label: 'En attente' },
      'Payée': { icon: faCheckCircle, variant: 'success', label: 'Payée' },
      'Annulée': { icon: faTimesCircle, variant: 'danger', label: 'Annulée' }
    };
    const c = config[statut] || config['En attente'];
    return (
      <Badge bg={c.variant} className="d-inline-flex align-items-center gap-1 px-3 py-1 rounded-pill" style={{ fontSize: '0.75rem', fontWeight: 500 }}>
        <FontAwesomeIcon icon={c.icon} size="xs" />
        {c.label}
      </Badge>
    );
  };

  // ============================================================
  // FILTRAGE
  // ============================================================
  const filteredFactures = useMemo(() => {
    return factures.filter(f => {
      const searchLower = searchText.toLowerCase();
      const matchText = !searchText ||
        (f.reference || '').toLowerCase().includes(searchLower) ||
        (f.entreprise_nom || '').toLowerCase().includes(searchLower) ||
        (f.offre_titre || '').toLowerCase().includes(searchLower);

      const matchStatut = !filterStatut || f.statut === filterStatut;

      let matchDate = true;
      if (dateDebut && dateFin) {
        const dateFacture = new Date(f.date_facture);
        const debut = new Date(dateDebut);
        const fin = new Date(dateFin);
        matchDate = dateFacture >= debut && dateFacture <= fin;
      } else if (dateDebut) {
        matchDate = new Date(f.date_facture) >= new Date(dateDebut);
      } else if (dateFin) {
        matchDate = new Date(f.date_facture) <= new Date(dateFin);
      }
      return matchText && matchStatut && matchDate;
    });
  }, [factures, searchText, filterStatut, dateDebut, dateFin]);

  const resetFilters = () => {
    setSearchText('');
    setFilterStatut('');
    setDateDebut('');
    setDateFin('');
  };

  // ============================================================
  // RENDU
  // ============================================================
  if (loading && !factures.length) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="text-secondary mt-2">Chargement des factures...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-4 text-danger">
        <FontAwesomeIcon icon={faExclamationTriangle} size="2x" className="mb-2" />
        <p>{error}</p>
        <button className="btn btn-outline-primary" onClick={() => fetchFactures(true)}>
          <FontAwesomeIcon icon={faSyncAlt} className="me-2" /> Réessayer
        </button>
      </div>
    );
  }

  return (
    <>
      <style>{`
        .factures-wrapper {
          margin-top: 1.5rem;
          background: radial-gradient(ellipse at 20% 30%, #132a4a, #0a0f1f);
          padding: 1.5rem;
          border-radius: 24px;
          min-height: 70vh;
        }
        .header-actions {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 1rem;
          margin-bottom: 1.8rem;
        }
        .header-actions h5 {
          font-weight: 700;
          color: #e8edff;
          margin: 0;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }
        .header-actions h5 .icon {
          color: #4f8cff;
        }
        .badge-source {
          font-size: 0.7rem;
          padding: 4px 14px;
          border-radius: 40px;
          background: rgba(79, 140, 255, 0.12);
          color: #4f8cff;
          border: 1px solid rgba(79, 140, 255, 0.1);
          font-weight: 500;
          white-space: nowrap;
        }
        .btn-glass {
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.06);
          color: #e8edff;
          border-radius: 40px;
          padding: 0.4rem 1rem;
          transition: all 0.2s;
          font-size: 0.9rem;
          backdrop-filter: blur(4px);
          cursor: pointer;
        }
        .btn-glass:hover:not(:disabled) {
          background: rgba(255,255,255,0.08);
          border-color: rgba(255,255,255,0.12);
          transform: translateY(-1px);
        }
        .btn-glass:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .filters-bar {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 0.8rem;
          margin-bottom: 1.5rem;
          background: rgba(255,255,255,0.02);
          border: 1px solid rgba(255,255,255,0.04);
          border-radius: 60px;
          padding: 0.6rem 1.2rem;
          backdrop-filter: blur(8px);
        }
        .filters-bar .filter-group {
          display: flex;
          align-items: center;
          gap: 0.4rem;
          flex: 1 1 180px;
          min-width: 140px;
        }
        .filters-bar .filter-group .filter-icon {
          color: #5e6f94;
          font-size: 0.8rem;
        }
        .filters-bar .filter-group input,
        .filters-bar .filter-group select {
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 40px;
          color: #e8edff;
          padding: 0.3rem 0.8rem;
          font-size: 0.85rem;
          outline: none;
          width: 100%;
          transition: border 0.2s;
        }
        .filters-bar .filter-group input:focus,
        .filters-bar .filter-group select:focus {
          border-color: #4f8cff;
        }
        .filters-bar .filter-group input::placeholder {
          color: #5e6f94;
        }
        .filters-bar .filter-group select option {
          background: #0b1428;
          color: #e8edff;
        }
        .filters-bar .filter-group.date-input {
          flex: 0 1 160px;
        }
        .filters-bar .filter-group.date-input input {
          padding: 0.3rem 0.5rem;
        }
        .filters-bar .reset-btn {
          background: transparent;
          border: none;
          color: #5e6f94;
          cursor: pointer;
          padding: 0.2rem 0.8rem;
          border-radius: 30px;
          transition: 0.2s;
          font-size: 0.85rem;
          white-space: nowrap;
        }
        .filters-bar .reset-btn:hover {
          background: rgba(255,255,255,0.05);
          color: #e8edff;
        }
        .filters-bar .reset-btn .icon {
          margin-right: 0.3rem;
        }

        .cards-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
          gap: 1.2rem;
          margin-top: 0.5rem;
        }
        .facture-card {
          background: rgba(255, 255, 255, 0.03);
          backdrop-filter: blur(8px);
          border: 1px solid rgba(255, 255, 255, 0.06);
          border-radius: 20px;
          padding: 1.2rem;
          transition: all 0.3s ease;
          display: flex;
          flex-direction: column;
        }
        .facture-card:hover {
          background: rgba(255, 255, 255, 0.06);
          border-color: rgba(79, 140, 255, 0.2);
          transform: translateY(-4px);
          box-shadow: 0 12px 32px rgba(0, 0, 0, 0.3);
        }
        .facture-card .card-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 0.5rem;
        }
        .facture-card .reference {
          font-weight: 600;
          color: #e8edff;
          font-size: 0.9rem;
        }
        .facture-card .reference code {
          background: rgba(255,255,255,0.04);
          padding: 0.1rem 0.4rem;
          border-radius: 6px;
          font-size: 0.85rem;
        }
        .facture-card .card-body {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 0.4rem;
        }
        .facture-card .info-row {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.9rem;
          color: #a5b4d9;
        }
        .facture-card .info-row .icon {
          color: #5e6f94;
          width: 18px;
        }
        .facture-card .info-row .value {
          color: #e8edff;
        }
        .facture-card .info-row .value.entreprise {
          font-weight: 500;
        }
        .facture-card .info-row .value.montant {
          color: #4ade80;
          font-weight: 600;
        }
        .facture-card .card-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-top: 0.8rem;
          padding-top: 0.8rem;
          border-top: 1px solid rgba(255,255,255,0.04);
          flex-wrap: wrap;
          gap: 0.5rem;
        }
        .facture-card .statut-select {
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 30px;
          color: #e8edff;
          padding: 0.2rem 0.6rem;
          font-size: 0.8rem;
          outline: none;
          cursor: pointer;
          transition: border 0.2s;
        }
        .facture-card .statut-select:focus {
          border-color: #4f8cff;
        }
        .facture-card .statut-select option {
          background: #0b1428;
          color: #e8edff;
        }
        .facture-card .statut-select:disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }
        .facture-card .actions {
          display: flex;
          gap: 0.3rem;
        }
        .facture-card .btn-icon {
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 30px;
          color: #e8edff;
          padding: 0.2rem 0.6rem;
          transition: 0.2s;
          font-size: 0.8rem;
          cursor: pointer;
        }
        .facture-card .btn-icon:hover {
          background: rgba(255,255,255,0.08);
        }
        .facture-card .btn-icon.danger:hover {
          background: rgba(239,68,68,0.15);
          border-color: rgba(239,68,68,0.2);
          color: #f87171;
        }
        .facture-card .locked-message {
          display: flex;
          align-items: center;
          gap: 0.4rem;
          color: #5e6f94;
          font-size: 0.8rem;
          padding: 0.2rem 0.5rem;
          background: rgba(255,255,255,0.02);
          border-radius: 30px;
        }
        .count-badge {
          background: rgba(255,255,255,0.04);
          border-radius: 30px;
          padding: 0.1rem 0.7rem;
          font-size: 0.75rem;
          color: #a5b4d9;
          margin-left: 0.5rem;
        }
        .hs-badge {
          background: rgba(251, 191, 36, 0.15);
          color: #fbbf24;
          padding: 0.1rem 0.6rem;
          border-radius: 30px;
          font-size: 0.7rem;
          font-weight: 500;
          border: 1px solid rgba(251,191,36,0.1);
          display: inline-flex;
          align-items: center;
          gap: 0.3rem;
          margin-left: 0.5rem;
        }
        .text-success { color: #4ade80; }
        .text-primary { color: #4f8cff; }
        .empty-state {
          text-align: center;
          padding: 3rem 0;
        }
        .empty-state .icon {
          font-size: 3rem;
          color: #5e6f94;
          opacity: 0.3;
          margin-bottom: 1rem;
        }

        @media (max-width: 768px) {
          .factures-wrapper { padding: 1rem; }
          .header-actions { flex-direction: column; align-items: stretch; }
          .header-actions .d-flex { justify-content: center; }
          .filters-bar {
            flex-direction: column;
            align-items: stretch;
            border-radius: 20px;
            padding: 0.8rem;
            gap: 0.6rem;
          }
          .filters-bar .filter-group { flex: 1 1 auto; min-width: unset; }
          .filters-bar .filter-group.date-input { flex: 1 1 auto; }
          .cards-grid { grid-template-columns: 1fr; }
          .facture-card .card-footer { flex-direction: column; align-items: stretch; }
          .facture-card .actions { justify-content: flex-end; }
        }
      `}</style>

      <div className="factures-wrapper">
        <div className="header-actions">
          <h5>
            <FontAwesomeIcon icon={faFileInvoice} className="icon" />
            Gestion des factures
            <span className="count-badge">{filteredFactures.length} factures</span>
          </h5>
          <div className="d-flex gap-2 align-items-center">
            <span className="badge-source">{dataSource === 'cache' ? '📦 Cache' : '🔄 Live'}</span>
            <button className="btn-glass" onClick={() => fetchFactures(true)} disabled={refreshing}>
              <FontAwesomeIcon icon={faSyncAlt} spin={refreshing} className="me-1" />
              {refreshing ? '...' : 'Actualiser'}
            </button>
          </div>
        </div>

        <div className="filters-bar">
          <div className="filter-group">
            <FontAwesomeIcon icon={faSearch} className="filter-icon" />
            <input
              type="text"
              placeholder="Rechercher..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </div>
          <div className="filter-group">
            <FontAwesomeIcon icon={faFilter} className="filter-icon" />
            <select
              value={filterStatut}
              onChange={(e) => setFilterStatut(e.target.value)}
            >
              <option value="">Tous statuts</option>
              <option value="En attente">En attente</option>
              <option value="Payée">Payée</option>
              <option value="Annulée">Annulée</option>
            </select>
          </div>
          <div className="filter-group date-input">
            <input
              type="date"
              placeholder="Du"
              value={dateDebut}
              onChange={(e) => setDateDebut(e.target.value)}
            />
          </div>
          <div className="filter-group date-input">
            <input
              type="date"
              placeholder="Au"
              value={dateFin}
              onChange={(e) => setDateFin(e.target.value)}
            />
          </div>
          {(searchText || filterStatut || dateDebut || dateFin) && (
            <button className="reset-btn" onClick={resetFilters}>
              <FontAwesomeIcon icon={faTimes} className="icon" />
              Réinitialiser
            </button>
          )}
        </div>

        {filteredFactures.length === 0 ? (
          <div className="empty-state">
            <FontAwesomeIcon icon={faFileInvoice} className="icon" />
            <p className="text-secondary">Aucune facture ne correspond à vos filtres.</p>
          </div>
        ) : (
          <div className="cards-grid">
            {filteredFactures.map(f => (
              <div key={f.id} className="facture-card">
                <div className="card-header">
                  <span className="reference">
                    <code>{f.reference}</code>
                    {f.heure_sup === 'oui' && parseFloat(f.montant_sup) > 0 && (
                      <span className="hs-badge">
                        <FontAwesomeIcon icon={faClockIcon} /> HS
                      </span>
                    )}
                  </span>
                  {getStatutBadge(f.statut)}
                </div>
                <div className="card-body">
                  <div className="info-row">
                    <FontAwesomeIcon icon={faBuilding} className="icon" />
                    <span className="value entreprise">{f.entreprise_nom || '—'}</span>
                  </div>
                  <div className="info-row">
                    <FontAwesomeIcon icon={faTag} className="icon" />
                    <span className="value">{f.offre_titre || '—'}</span>
                  </div>
                  <div className="info-row">
                    <FontAwesomeIcon icon={faUsers} className="icon" />
                    <span className="value">{f.nombre_candidats} candidat(s)</span>
                  </div>
                  <div className="info-row">
                    <FontAwesomeIcon icon={faDollarSign} className="icon" />
                    <span className="value montant">{formatCurrency(f.montant_ttc)}</span>
                  </div>
                  <div className="info-row" style={{ fontSize: '0.8rem', color: '#5e6f94' }}>
                    <span>Date: {new Date(f.date_facture).toLocaleDateString()}</span>
                  </div>
                </div>
                <div className="card-footer">
                   <Button
                        variant="outline-primary"
                        size="sm"
                        className="btn-icon"
                        onClick={() => openInvoiceModal(f.id)}
                        disabled={loadingInvoice}
                      >
                        <FontAwesomeIcon icon={faEye} /> Voir facture
                      </Button>
                  {f.statut === 'Payée' ? (
                    <>
                     
                      <div className="locked-message">
                        <FontAwesomeIcon icon={faLock} />
                        Payée
                      </div>
                    </>
                  ) : (
                    <>
                      <Form.Select
                        size="sm"
                        className="statut-select"
                        value={f.statut || 'En attente'}
                        onChange={(e) => handleStatutChange(f.id, e.target.value)}
                        disabled={updating[f.id]}
                        style={{ width: 'auto' }}
                      >
                        <option value="En attente">En attente</option>
                        <option value="Payée">Payée</option>
                        <option value="Annulée">Annulée</option>
                      </Form.Select>
                      <div className="actions">
                        <button
                          className="btn-icon danger"
                          onClick={() => handleDelete(f.id, f.statut)}
                          disabled={deleting[f.id] || updating[f.id]}
                          title="Supprimer"
                        >
                          {deleting[f.id] ? <Spinner animation="border" size="sm" /> : <FontAwesomeIcon icon={faTrash} />}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ============================================================
          MODAL APERÇU FACTURE (avec jsPDF) – CORRIGÉ
          ============================================================ */}
      <Modal show={showInvoiceModal} onHide={() => setShowInvoiceModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faFileInvoice} className="me-2 text-primary" />
            Facture {selectedFacture?.reference}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto', background: '#f9fafb' }}>
          {loadingInvoice ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p>Chargement de la facture...</p>
            </div>
          ) : selectedFacture ? (
            <div ref={invoiceRef} style={{ padding: '20px', background: 'white', color: '#333', fontFamily: 'Arial, sans-serif' }}>
              {/* En-tête */}
              <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '2px solid #333', paddingBottom: '10px' }}>
                <div>
                  {companyParams?.logoUrl ? (
                    <img src={companyParams.logoUrl} alt="Logo" style={{ maxHeight: '60px' }} />
                  ) : (
                    <img src={Logo} alt="Logo" style={{ maxHeight: '60px' }} />
                  )}
                </div>
                <div style={{ textAlign: 'right' }}>
                  <h4>{companyParams?.nom || 'BISENTER SARL'}</h4>
                  <p style={{ margin: 0, fontSize: '12px' }}>{companyParams?.adresse || ''}</p>
                  <p style={{ margin: 0, fontSize: '12px' }}>Tél: {companyParams?.telephone || ''}</p>
                  <p style={{ margin: 0, fontSize: '12px' }}>Email: {companyParams?.email || ''}</p>
                  <p style={{ margin: 0, fontSize: '12px' }}>RCCM: {companyParams?.rccm || ''}</p>
                  <p style={{ margin: 0, fontSize: '12px' }}>ID.NAT: {companyParams?.idnat || ''}</p>
                  <p style={{ margin: 0, fontSize: '12px' }}>N° Impôt: {companyParams?.nimpot || ''}</p>
                </div>
              </div>

              <h2 style={{ textAlign: 'center', margin: '20px 0' }}>FACTURE</h2>

              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '15px' }}>
                <div>
                  <p><strong>Facture N°</strong> {selectedFacture.reference}</p>
                  <p><strong>Date</strong> {new Date(selectedFacture.date_facture).toLocaleDateString()}</p>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <p><strong>Client</strong> {selectedFacture.entreprise_nom || '—'}</p>
                  <p><strong>Offre</strong> {selectedFacture.offre_titre || '—'}</p>
                </div>
              </div>

              <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '15px' }}>
                <thead>
                  <tr style={{ background: '#f0f0f0' }}>
                    <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left' }}>Candidat</th>
                    <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left' }}>Poste</th>
                    <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left' }}>Contrat</th>
                    <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'right' }}>Salaire (USD)</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedFacture.details?.candidats?.map((c, idx) => (
                    <tr key={idx}>
                      <td style={{ padding: '6px', border: '1px solid #ddd' }}>{c.prenom} {c.nom}</td>
                      <td style={{ padding: '6px', border: '1px solid #ddd' }}>{selectedFacture.offre_titre}</td>
                      <td style={{ padding: '6px', border: '1px solid #ddd' }}>{selectedFacture.details?.type_contrat || '—'}</td>
                      <td style={{ padding: '6px', border: '1px solid #ddd', textAlign: 'right' }}>{parseFloat(c.salaire).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* ---- HEURES SUPPLÉMENTAIRES (affichage corrigé) ---- */}
              {selectedFacture.heure_sup === 'oui' && parseFloat(selectedFacture.montant_sup) > 0 && (
  <div style={{ textAlign: 'right', marginBottom: '5px', padding: '8px', background: '#f8f9fa', borderRadius: '6px' }}>
    <p style={{ margin: 0 }}>
      <strong>Heures supplémentaires :</strong> {parseFloat(selectedFacture.montant_sup).toFixed(2)} USD
    </p>
    <p style={{ margin: '2px 0 0 0', fontSize: '12px', color: '#6c757d' }}>
      <em>Ce montant est déjà intégré au total TTC ci-dessous.</em>
    </p>
  </div>
)}

              <div style={{ textAlign: 'right', marginTop: '10px' }}>
                <p><strong>Montant HT</strong> {parseFloat(selectedFacture.montant_ht).toFixed(2)} USD</p>
                <p><strong>TVA ({selectedFacture.tva}%)</strong> {((selectedFacture.montant_ht * selectedFacture.tva) / 100).toFixed(2)} USD</p>
                <p  style={{ fontSize: '18px', fontWeight: 'bold' }}><strong>Total TTC</strong> {parseFloat(selectedFacture.montant_ttc).toFixed(2)} USD</p>
              </div>

              <div style={{ marginTop: '30px', textAlign: 'center', fontSize: '12px', color: '#aaa', borderTop: '1px solid #ddd', paddingTop: '10px' }}>
                Merci de votre confiance – www.bisenter-rdc.com
              </div>
            </div>
          ) : (
            <p className="text-center">Aucune donnée</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowInvoiceModal(false)}>
            Fermer
          </Button>
          <Button variant="primary" onClick={generatePDF} disabled={!selectedFacture || pdfGenerating}>
            {pdfGenerating ? (
              <><Spinner as="span" animation="border" size="sm" className="me-2" /> Génération...</>
            ) : (
              <><FontAwesomeIcon icon={faDownload} className="me-2" /> Télécharger PDF</>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default GestionFactures;